/**
 * Container — Wrapper for Container-mode rows (Inner Row architecture)
 *
 * In Canvas v2.0, a container-mode row wraps multiple inner rows.
 * The Container renders them vertically (flex column) and applies
 * the parent row settings (backgroundColor, padding, sticky, etc.)
 */
import React from 'react';
import type { BlockComponentProps } from '../../renderer/BlockRegistry';

type BoxSides = { top?: number; bottom?: number; left?: number; right?: number };
const asBox = (v: unknown): BoxSides | undefined =>
  v && typeof v === 'object' ? (v) : undefined;
const asStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const asNum = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

export const Container: React.FC<BlockComponentProps & { 'data-block-type'?: string, 'data-block-id'?: string }> = ({ data, children, style, className, 'data-block-type': dataBlockType, 'data-block-id': dataBlockId }) => {
  const padding = asBox(data?.padding);
  const paddingStyle: React.CSSProperties = padding ? {
    paddingTop: padding.top != null ? `${padding.top}px` : undefined,
    paddingBottom: padding.bottom != null ? `${padding.bottom}px` : undefined,
    paddingLeft: padding.left != null ? `${padding.left}px` : undefined,
    paddingRight: padding.right != null ? `${padding.right}px` : undefined,
  } : {};

  const margin = asBox(data?.margin);
  const marginStyle: React.CSSProperties = margin ? {
    marginTop: margin.top != null ? `${margin.top}px` : undefined,
    marginBottom: margin.bottom != null ? `${margin.bottom}px` : undefined,
    marginLeft: margin.left != null ? `${margin.left}px` : undefined,
    marginRight: margin.right != null ? `${margin.right}px` : undefined,
  } : {};

  const stickyTop = asNum(data?.stickyTop);
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: asStr(data?.backgroundColor) || undefined,
    // Sticky support for header containers
    position: data?.sticky ? 'sticky' : undefined,
    top: stickyTop != null ? `${stickyTop}px` : undefined,
    zIndex: asNum(data?.zIndex) || undefined,
    ...paddingStyle,
    ...marginStyle,
    ...style,
  };

  const dataId: string | undefined = typeof data?.id === 'string' || typeof data?.id === 'number' ? String(data.id) : undefined;
  const containerId = dataId ? `container-${dataId}` : undefined;

  return (
    <div
      id={containerId}
      style={containerStyle}
      className={`${typeof data?.customClasses === 'string' ? data.customClasses : ''} ${className ?? ''}`.trim()}
      data-block-type={dataBlockType}
      data-block-id={dataBlockId}
    >
      {children}
    </div>
  );
};
